import React, { useEffect, useState, forwardRef } from 'react';
import {
  Typography, Paper, Button, CircularProgress, Snackbar, Alert as MuiAlert, Dialog,
  DialogActions, DialogContent, DialogTitle, TextField, Box, Grid, Divider
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import axiosInstance from '../../api/axiosConfig';
import PropTypes from 'prop-types';

// Styled Components
const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  background: theme.palette.background.paper,
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  textTransform: 'none',
  fontWeight: 500,
  padding: theme.spacing(1, 3),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const DetailItemLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  minWidth: '120px',
  marginRight: theme.spacing(1),
}));

const DetailItemValue = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.text.primary,
  fontSize: '0.9rem',
  wordBreak: 'break-word',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    '& fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.dark,
      borderWidth: 2,
    },
  },
}));

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EmployeeIdentifier = ({ employeeId, showSnackbar: parentShowSnackbar }) => {
  const theme = useTheme();
  const [identifier, setIdentifier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    panNumber: '',
    aadharNumber: '',
    uanNumber: '',
    esiNumber: '',
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!employeeId || employeeId === 'unknown') {
      setLoading(false);
      showSnackbar('Invalid employee ID.', 'error');
      return;
    }
    fetchIdentifier();
    // eslint-disable-next-line
  }, [employeeId]);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
    if (parentShowSnackbar) {
      parentShowSnackbar(message, severity);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const fetchIdentifier = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/employee-identifiers/${employeeId}`);
      setIdentifier(response.data || null);
    } catch (error) {
      handleError(error, 'Failed to fetch identifier.');
      setIdentifier(null);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error, defaultMessage) => {
    let message = defaultMessage;
    if (error.response) {
      const { data } = error.response;
      if (data.message) {
        message = data.message; // Use backend message if available
      } else if (data.errors) {
        message = data.errors.join(', '); // Handle validation errors
      } else {
        message = `Error: ${error.response.statusText || defaultMessage}`;
      }
    } else if (error.request) {
      message = 'Error: No response received from server.';
    } else {
      message = `Error: ${error.message}`;
    }
    console.error("API Error:", error);
    showSnackbar(message, 'error');
  };

  const handleOpenDialog = () => {
    setFormErrors({});
    setFormData({
      panNumber: identifier?.panNumber || '',
      aadharNumber: identifier?.aadharNumber || '',
      uanNumber: identifier?.uanNumber || '',
      esiNumber: identifier?.esiNumber || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.panNumber.trim()) {
      errors.panNumber = 'PAN Number is required';
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber.trim())) {
      errors.panNumber = 'Invalid PAN Number format';
    }
    if (!formData.aadharNumber.trim()) {
      errors.aadharNumber = 'Aadhar Number is required';
    } else if (!/^\d{12}$/.test(formData.aadharNumber.trim())) {
      errors.aadharNumber = 'Aadhar Number must be 12 digits';
    }
    if (formData.uanNumber && !/^\d{12}$/.test(formData.uanNumber.trim())) {
      errors.uanNumber = 'UAN Number must be 12 digits';
    }
    if (formData.esiNumber && !/^\d{10}$/.test(formData.esiNumber.trim())) {
      errors.esiNumber = 'ESI Number must be 10 digits';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (identifier) {
        await axiosInstance.put(`/api/employee-identifiers/${employeeId}`, {
          ...formData,
          employeeId,
        });
        showSnackbar('Identifier updated successfully.', 'success');
      } else {
        await axiosInstance.post('/api/employee-identifiers', {
          ...formData,
          employeeId,
        });
        showSnackbar('Identifier added successfully.', 'success');
      }
      fetchIdentifier();
      handleCloseDialog();
    } catch (error) {
      handleError(error, `Failed to ${identifier ? 'update' : 'add'} identifier.`);
    }
  };

  const handleDelete = async () => {
    if (!identifier) return;
    try {
      await axiosInstance.delete(`/api/employee-identifiers/${identifier.identifierId}`);
      showSnackbar('Identifier deleted successfully.', 'success');
      setIdentifier(null);
    } catch (error) {
      handleError(error, 'Failed to delete identifier.');
    }
  };

  const renderDetailItem = (label, value) => {
    let displayValue;
    if (value === null || value === undefined || value === '') {
      displayValue = <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>Not Available</Typography>;
    } else {
      displayValue = <DetailItemValue>{value}</DetailItemValue>;
    }

    return (
      <Grid item xs={12} sm={6} md={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DetailItemLabel>{label}:</DetailItemLabel>
          {displayValue}
        </Box>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <SectionPaper variant="outlined">
      <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
        Document Details
      </Typography>
      <Divider sx={{ mb: 3, borderColor: theme.palette.divider }} />

      {identifier ? (
        <>
          <Grid container spacing={3}>
            {renderDetailItem('PAN Number', identifier.panNumber)}
            {renderDetailItem('Aadhar Number', identifier.aadharNumber)}
            {renderDetailItem('UAN Number', identifier.uanNumber)}
            {renderDetailItem('ESI Number', identifier.esiNumber)}
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <StyledButton
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              onClick={handleOpenDialog}
            >
              Edit
            </StyledButton>
            <StyledButton
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDelete}
            >
              Delete
            </StyledButton>
          </Box>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No identifier details available.
          </Typography>
          <StyledButton
            variant="contained"
            color="primary"
            startIcon={<Edit />}
            onClick={handleOpenDialog}
          >
            Add Identifier
          </StyledButton>
        </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiPaper-root': { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={600}>
          {identifier ? 'Edit Identifier' : 'Add Identifier'}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <StyledTextField
                label="PAN Number *"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.panNumber}
                helperText={formErrors.panNumber}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Aadhar Number *"
                name="aadharNumber"
                value={formData.aadharNumber}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.aadharNumber}
                helperText={formErrors.aadharNumber}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="UAN Number"
                name="uanNumber"
                value={formData.uanNumber}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.uanNumber}
                helperText={formErrors.uanNumber}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="ESI Number"
                name="esiNumber"
                value={formData.esiNumber}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.esiNumber}
                helperText={formErrors.esiNumber}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: '20px 24px' }}>
          <StyledButton
            variant="outlined"
            color="secondary"
            onClick={handleCloseDialog}
          >
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            {identifier ? 'Update' : 'Add'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </SectionPaper>
  );
};

EmployeeIdentifier.propTypes = {
  employeeId: PropTypes.string.isRequired,
  showSnackbar: PropTypes.func,
};

export default EmployeeIdentifier;